/**
 * /invitacion-premium — A/B: BioTemplatePremiumSimple vs ReelsFeed.
 *
 * Assignment order:
 * 1. URL `?invite_layout=reels_feed|premium_simple`
 * 2. `VITE_PREMIUM_INVITE_LAYOUT_OVERRIDE` (reels_feed | premium_simple)
 * 3. Session cache `premium_invite_layout_assignment` (sticky per tab session)
 * 4. PostHog multivariate `premium_invite_layout` → `reels_feed` | `premium_simple`
 * 5. `VITE_PREMIUM_INVITE_REELS_PERCENT` (0–100): random bucket in session `premium_invite_ab_bucket`
 */

import { getFeatureFlagPostHog, trackFeatureFlagPostHog } from "@/lib/posthogAnalytics";
import {
  POSTHOG_FLAG_PREMIUM_INVITE_LAYOUT,
  normalizePremiumInviteLayoutFlagValue,
} from "@/lib/premiumInvitePosthogFlags";

export const PREMIUM_INVITE_LAYOUT_FLAG = POSTHOG_FLAG_PREMIUM_INVITE_LAYOUT;

export type PremiumInviteLayout = "premium_simple" | "reels_feed";

export const PREMIUM_INVITE_LAYOUT_STORAGE_KEY = "premium_invite_layout_assignment";

export type PremiumInviteLayoutSource =
  | "url_param"
  | "env_override"
  | "session_cached"
  | "posthog"
  | "percentage"
  | "admin_force"
  | "admin_disabled";

function captureAssignment(
  layout: PremiumInviteLayout,
  source: PremiumInviteLayoutSource
): void {
  if (typeof window === "undefined") return;
  try {
    (
      window as Window & { posthog?: { capture?: (e: string, p?: object) => void } }
    ).posthog?.capture?.("premium_invite_layout_assigned", {
      layout,
      source,
      page_path: window.location.pathname,
    });
  } catch {
    /* no-op */
  }
}

export function persistPremiumInviteLayout(layout: PremiumInviteLayout): void {
  try {
    sessionStorage.setItem(PREMIUM_INVITE_LAYOUT_STORAGE_KEY, layout);
  } catch {
    /* no-op */
  }
}

function readUrlOverride(): PremiumInviteLayout | null {
  if (typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search)
    .get("invite_layout")
    ?.trim()
    .toLowerCase();
  if (q === "reels" || q === "reels_feed") return "reels_feed";
  if (q === "simple" || q === "premium_simple") return "premium_simple";
  return null;
}

function readEnvOverride(): PremiumInviteLayout | null {
  const o = import.meta.env.VITE_PREMIUM_INVITE_LAYOUT_OVERRIDE?.trim().toLowerCase();
  if (o === "reels" || o === "reels_feed") return "reels_feed";
  if (o === "simple" || o === "premium_simple") return "premium_simple";
  return null;
}

export function readPostHogPremiumInviteLayout(): PremiumInviteLayout | null {
  const v = getFeatureFlagPostHog(PREMIUM_INVITE_LAYOUT_FLAG);
  return normalizePremiumInviteLayoutFlagValue(v);
}

export function percentReelsTraffic(): number {
  const n = Number(import.meta.env.VITE_PREMIUM_INVITE_REELS_PERCENT ?? "0");
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** Rollout %: explicit DB/admin value wins; otherwise env `VITE_PREMIUM_INVITE_REELS_PERCENT`. */
export function resolveRolloutPercent(remotePercent?: number | null): number {
  if (remotePercent != null && Number.isFinite(remotePercent)) {
    return Math.min(100, Math.max(0, Math.round(remotePercent)));
  }
  return percentReelsTraffic();
}

export function getPremiumInviteLayoutFromPercent(
  overridePercent?: number | null,
): PremiumInviteLayout {
  const pct = resolveRolloutPercent(
    overridePercent === undefined ? null : overridePercent,
  );
  if (pct <= 0) return "premium_simple";
  if (pct >= 100) return "reels_feed";
  try {
    let bucket = sessionStorage.getItem("premium_invite_ab_bucket");
    if (bucket == null) {
      bucket = String(Math.floor(Math.random() * 100));
      sessionStorage.setItem("premium_invite_ab_bucket", bucket);
    }
    return Number(bucket) < pct ? "reels_feed" : "premium_simple";
  } catch {
    return "premium_simple";
  }
}

export function finishPremiumInviteLayoutAssignment(
  layout: PremiumInviteLayout,
  source: PremiumInviteLayoutSource
): void {
  persistPremiumInviteLayout(layout);
  if (source === "posthog") {
    trackFeatureFlagPostHog(PREMIUM_INVITE_LAYOUT_FLAG, layout);
  }
  captureAssignment(layout, source);
}
